import { Lang } from "@/lib/i18n";
import { SubPageContent } from "@/components/common/SubPageLayout";

export const COPY: Record<Lang, SubPageContent> = {
  en: {
    breadcrumbKey: "TwillDutchWeave",
    relatedTopic: "woven",
    hero: {
      categoryBadge: "Dutch Weave Filter Cloth",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Twill Dutch Weave Wire Mesh",
      lead: "Utilizing a two-over / two-under twilled warp progression with tightly packed double weft wire density, Twill Dutch Weave delivers micro-pore filtration down to 1 µm under demanding hydraulic differential pressures with zero direct line of sight.",
      secondaryCta: {
        label: "View All Filter Cloth",
        to: "/FilterClothPage",
      },
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/twill-dutch-weave.webp",
        alt: "Twill Dutch Weave stainless steel filter cloth macro view",
      },
      cardNote: "2/2 twilled double-weft media",
      cardLink: {
        label: "Specifications",
        to: "/TwillDutchWeavePage#specifications",
      },
      specs: [
        { label: "Mesh Range", value: "20×250 – 400×2800" },
        { label: "Retention Rating", value: "~1 – 225 µm nominal" },
        { label: "Weave Structure", value: "2/2 Twilled Dutch pack" },
        { label: "Standard Alloys", value: "AISI 304, 304L, 316, 316L" },
      ],
    },
    overview: {
      eyebrow: "Sub-Micronic Particle Retention",
      heading: "Double Weft Density for Demanding Hydraulic Environments",
      paragraphs: [
        "Twill Dutch Weave (also referred to as köpertresse or twilled dutch wire cloth) advances the plain Dutch concept by incorporating a twilled 2/2 wire passage. This weaving technique relieves localized wire curvature stresses, enabling the loom to pack twice as many weft wires per linear unit compared to plain Dutch weaving.",
        "The resulting fabric features a dense double-layer of shute wires that completely blocks straight-through optical light. Process fluid is forced through tightly curved, tortuous microscopic channels formed between overlapping wires, delivering dependable cutoff thresholds down to 1 micron.",
        "Because twilled Dutch cloth withstands severe differential pressure without opening aperture shifts, it serves as the critical media in polymer melt spinning packs, hydraulic oil filters, aviation fuel strainers, and aerospace cryogenic systems. Specifications at 325×2300 and finer are strictly woven in twill weave.",
      ],
      keyPoints: [
        "Twilled warp wire arrangement allows double the weft density with zero direct line of sight, delivering micro-retention down to 1 micron under severe differential pressures.",
        "Tortuous curved micro-channels provide high dirt-holding capacity while resisting breakthrough under pulsing flow.",
        "Standard manufacturing mesh range from 20×250 to 400×2800 (nominal ~1–225 µm; specifications of 325×2300 and finer are twill, never plain).",
      ],
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/twill-dutch-weave.webp",
        alt: "Stainless steel twill dutch weave wire cloth micro detail",
      },
    },
    specTable: {
      heading: "Twill Dutch Weave Standard Specifications",
      intro: "Manufacturing parameters for industrial stainless steel twill dutch weave wire cloth conforming to ASTM E2016 and ISO 9044.",
      columns: [
        { key: "mesh", label: "Mesh (Warp × Weft)", align: "left" },
        { key: "warpWire", label: "Warp Wire (mm)", align: "right" },
        { key: "weftWire", label: "Weft Wire (mm)", align: "right" },
        { key: "retention", label: "Nominal Micron Rating (µm)", align: "right" },
        { key: "standard", label: "Compliance Standard", align: "right" },
      ],
      rows: [
        {
          id: "row-td-1",
          mesh: "20 × 250",
          warpWire: "0.25",
          weftWire: "0.20",
          retention: "200 – 225",
          standard: "ASTM E2016",
        },
        {
          id: "row-td-2",
          mesh: "30 × 350",
          warpWire: "0.20",
          weftWire: "0.14",
          retention: "140 – 160",
          standard: "ASTM E2016",
        },
        {
          id: "row-td-3",
          mesh: "80 × 700",
          warpWire: "0.10",
          weftWire: "0.07",
          retention: "40 – 45",
          standard: "ISO 9044",
        },
        {
          id: "row-td-4",
          mesh: "165 × 1400",
          warpWire: "0.07",
          weftWire: "0.04",
          retention: "15 – 18",
          standard: "ASTM E2016",
        },
        {
          id: "row-td-5",
          mesh: "200 × 1400",
          warpWire: "0.07",
          weftWire: "0.04",
          retention: "10 – 12",
          standard: "ASTM E2016",
        },
        {
          id: "row-td-6",
          mesh: "325 × 2300",
          warpWire: "0.038",
          weftWire: "0.025",
          retention: "2 – 3",
          standard: "ASTM E2016",
        },
        {
          id: "row-td-7",
          mesh: "400 × 2800",
          warpWire: "0.030",
          weftWire: "0.018",
          retention: "1 – 2",
          standard: "ASTM E2016",
        },
      ],
      caption: "Nominal filtration accuracy based on glass bead retention and bubble point measurement per ASTM E2016 standards.",
    },
    features: {
      heading: "Technical & Mechanical Advantages",
      intro: "Core engineering attributes of twilled Dutch weave in fine-separation systems.",
      items: [
        {
          title: "Highest Packing Density",
          text: "2/2 twill movement accommodates the highest number of cross wires per inch among all standard single-layer woven wire structures.",
        },
        {
          title: "Extreme Differential Resistance",
          text: "Multiple nested wire contact zones distribute fluid shear stresses evenly across the cloth plane, preventing pore dilation.",
        },
        {
          title: "Tortuous Particle Capture",
          text: "Three-dimensional curved pore paths trap non-spherical particles, providing reliable retention without media migration.",
        },
        {
          title: "High-Temperature Alloys",
          text: "Fabricated from precision-drawn AISI 304, 304L, 316, and 316L stainless steel wires for thermal and chemical stability.",
        },
      ],
    },
    related: {
      heading: "Related Dutch Weave Products",
      intro: "Explore alternative Dutch weave configurations for different mechanical and flow criteria.",
      cards: [
        {
          title: "Plain Dutch Weave",
          text: "Heavy warp wires paired with compacted fine shute wires produce uniform triangular pores for predictable particulate retention and high cake discharge.",
          to: "/PlainDutchWeavePage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp",
            alt: "Plain Dutch Weave wire cloth",
          },
          badge: "8×85 – 80×400 (to 165×1200)",
          linkLabel: "View Plain Dutch",
        },
        {
          title: "Reverse Dutch Weave",
          text: "Dense warp wires paired with heavier spaced weft wires deliver exceptional longitudinal tensile strength for continuous screen belts and high-pressure polymer melt filtration.",
          to: "/ReverseDutchWeavePage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-macro-photograph-of-reverse-dutch-52764d.png",
            alt: "Reverse Dutch Weave wire cloth",
          },
          badge: "72×15 – 325×38",
          linkLabel: "View Reverse Dutch",
        },
        {
          title: "Filter Cloth Overview",
          text: "Comprehensive family overview detailing Dutch weave engineering characteristics, alloy selections, and fabrication options.",
          to: "/FilterClothPage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp?v=djofi647st4",
            alt: "Filter cloth overview",
          },
          badge: "1 µm – 310 µm",
          linkLabel: "View Parent Page",
        },
      ],
    },
    cta: {
      id: "twill-dutch-cta",
      eyebrow: "Precision Micro-Filtration",
      heading: "Order High-Precision Twill Dutch Weave Wire Mesh",
      lead: "MutualFilter supplies full master rolls, slit ribbons, and multi-layer stamped filter discs tailored to your operating specifications.",
      secondaryLabel: "Explore Filter Cloth",
      secondaryTo: "/FilterClothPage",
      note: "Material test reports and compliance certificates available when specified in the order.",
    },
  },
  zh: {
    breadcrumbKey: "TwillDutchWeave",
    relatedTopic: "woven",
    hero: {
      categoryBadge: "荷兰编织工业滤布",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "斜纹荷兰编织丝网",
      lead: "采用二上二下斜纹走线方式配合极高密度的双向纬丝排布，斜纹荷兰编织（斜纹密纹网）在严苛的高压差工况下实现低至 1 微米的精密拦截，且完全无直通透光通道。",
      secondaryCta: {
        label: "查看滤布总览",
        to: "/FilterClothPage",
      },
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/twill-dutch-weave.webp",
        alt: "不锈钢斜纹荷兰编织滤布微距拍摄",
      },
      cardNote: "2/2 斜纹双层密纬结构",
      cardLink: {
        label: "技术参数表",
        to: "/TwillDutchWeavePage#specifications",
      },
      specs: [
        { label: "目数范围", value: "20×250 – 400×2800" },
        { label: "公称精度", value: "~1 – 225 µm nominal" },
        { label: "编织形式", value: "2/2 斜纹双丝致密重叠" },
        { label: "标准材质", value: "AISI 304, 304L, 316, 316L" },
      ],
    },
    overview: {
      eyebrow: "微米级高精度固液分离",
      heading: "双倍纬丝密度，专为高压差苛刻工况设计",
      paragraphs: [
        "斜纹荷兰编织（俗称斜纹密纹网或斜纹席型网）在平纹荷兰编织的基础上，引入了 2/2 斜纹编织方法。该走线方式显著降低了金属丝在交织点的局部弯折应力，使得织机能够在单位长度内敲入相比平纹荷兰编织多达一倍数量的细纬丝。",
        "由此形成的滤网结构呈现紧密重叠的双层纬丝形态，彻底阻断了直射光线。流体被迫通过相邻微细金属丝交错所形成的曲折立体微孔通道，稳定实现低至 1 微米的微粒精密拦截。",
        "由于斜纹荷兰编织能够承受恶劣的高压差与周期性水锤冲击而不会发生网孔滑移变形，它被广泛用于化纤纺丝聚合物熔体滤芯、航空液压油滤器、精密燃油过滤系统等。325×2300 及以上的高目数规格均为斜纹编织，无平纹形式。",
      ],
      keyPoints: [
        "结合斜纹走线与双向压紧的极高纬丝密度，形成无直射光透孔的曲折立体通道，在高压差恶劣工况下稳定实现低至 1 微米精密截留。",
        "曲折立体的微米级流道具有出色的杂质容纳能力与抗流体脉冲冲击性能。",
        "标准制造目数覆盖 20×250 至 400×2800 (公称精度 ~1–225 µm；325×2300 及更细规格均为斜纹编织)。",
      ],
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/twill-dutch-weave.webp",
        alt: "不锈钢斜纹密纹网编织纹理细节",
      },
    },
    specTable: {
      heading: "斜纹荷兰编织常用技术规格表",
      intro: "符合 ASTM E2016 及 ISO 9044 标准的不锈钢斜纹荷兰编织标准制造规格代表数据。",
      columns: [
        { key: "mesh", label: "目数 (经 × 纬)", align: "left" },
        { key: "warpWire", label: "经丝丝径 (mm)", align: "right" },
        { key: "weftWire", label: "纬丝丝径 (mm)", align: "right" },
        { key: "retention", label: "公称精度 (µm)", align: "right" },
        { key: "standard", label: "执行标准", align: "right" },
      ],
      rows: [
        {
          id: "row-td-1",
          mesh: "20 × 250",
          warpWire: "0.25",
          weftWire: "0.20",
          retention: "200 – 225",
          standard: "ASTM E2016",
        },
        {
          id: "row-td-2",
          mesh: "30 × 350",
          warpWire: "0.20",
          weftWire: "0.14",
          retention: "140 – 160",
          standard: "ASTM E2016",
        },
        {
          id: "row-td-3",
          mesh: "80 × 700",
          warpWire: "0.10",
          weftWire: "0.07",
          retention: "40 – 45",
          standard: "ISO 9044",
        },
        {
          id: "row-td-4",
          mesh: "165 × 1400",
          warpWire: "0.07",
          weftWire: "0.04",
          retention: "15 – 18",
          standard: "ASTM E2016",
        },
        {
          id: "row-td-5",
          mesh: "200 × 1400",
          warpWire: "0.07",
          weftWire: "0.04",
          retention: "10 – 12",
          standard: "ASTM E2016",
        },
        {
          id: "row-td-6",
          mesh: "325 × 2300",
          warpWire: "0.038",
          weftWire: "0.025",
          retention: "2 – 3",
          standard: "ASTM E2016",
        },
        {
          id: "row-td-7",
          mesh: "400 × 2800",
          warpWire: "0.030",
          weftWire: "0.018",
          retention: "1 – 2",
          standard: "ASTM E2016",
        },
      ],
      caption: "公称截留精度符合 ASTM E2016 及 ISO 9044 玻璃微珠与气泡点标准测定方法。",
    },
    features: {
      heading: "结构与工艺技术优势",
      intro: "斜纹荷兰编织丝网在高精度流体净化中的关键技术优势。",
      items: [
        {
          title: "极高的纬丝堆叠密度",
          text: "2/2 斜纹走线实现了单层编织丝网中最高的线材密度，显著提升微孔截留精度。",
        },
        {
          title: "优异的耐高压差性能",
          text: "重叠紧锁的金属线接触面有效分散了流体剪切力，在高压强冲击下保持孔隙几何稳定。",
        },
        {
          title: "曲折深层拦截机制",
          text: "立体弯曲微孔通道能有效拦截不规则非球形颗粒，杜绝介质脱落与杂质穿透。",
        },
        {
          title: "高洁净耐蚀不锈钢",
          text: "采用精拔 AISI 304、304L、316 及 316L 不锈钢线材编织，确保在高温与腐蚀介质中长期稳定。",
        },
      ],
    },
    related: {
      heading: "相关荷兰编织产品系列",
      intro: "对比斜纹荷兰编织与同系列其它编织结构的技术特性。",
      cards: [
        {
          title: "平纹荷兰编织",
          text: "粗经丝配合紧密敲紧的细纬丝形成规则三角微孔流道，兼具稳定的固体颗粒拦截精度与良好的表面滤饼剥离特性。",
          to: "/PlainDutchWeavePage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp",
            alt: "平纹荷兰编织工业滤布",
          },
          badge: "8×85 – 80×400 (最高至 165×1200)",
          linkLabel: "查看平纹荷兰编织",
        },
        {
          title: "反向荷兰编织",
          text: "高密度紧排细经丝跨越较粗的间距纬丝，提供出色的纵向抗拉强度，专为自动换网器连续过滤带与高温高压聚合物熔体挤出设计。",
          to: "/ReverseDutchWeavePage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-macro-photograph-of-reverse-dutch-52764d.png",
            alt: "反向荷兰编织工业滤布",
          },
          badge: "72×15 – 325×38",
          linkLabel: "查看反向荷兰编织",
        },
        {
          title: "工业滤布总览",
          text: "查看荷兰编织工业滤布家族整体技术对比、材质选择指南及深加工形态。",
          to: "/FilterClothPage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp?v=djofi647st4",
            alt: "工业滤布产品总览",
          },
          badge: "1 µm – 310 µm",
          linkLabel: "返回上级页面",
        },
      ],
    },
    cta: {
      id: "twill-dutch-cta",
      eyebrow: "高精度微孔滤布",
      heading: "获取不锈钢斜纹荷兰编织丝网技术方案",
      lead: "MutualFilter 提供工业母卷、精密分切带卷及多层冲压复合滤片等定制加工服务。",
      secondaryLabel: "返回工业滤布总览",
      secondaryTo: "/FilterClothPage",
      note: "材质检验报告可按订单要求提供。",
    },
  },
  es: {
    breadcrumbKey: "TwillDutchWeave",
    relatedTopic: "woven",
    hero: {
      categoryBadge: "Tela Filtrante Dutch",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Malla Twill Dutch Weave",
      lead: "Utilizando un patrón en sarga de dos arriba y dos abajo con densidad de trama duplicada, el tejido Twill Dutch ofrece retención micrométrica de hasta 1 µm bajo altas presiones diferenciales sin paso de luz directo.",
      secondaryCta: {
        label: "Ver Tela Filtrante",
        to: "/FilterClothPage",
      },
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/twill-dutch-weave.webp",
        alt: "Detalle de malla Twill Dutch Weave en acero inoxidable",
      },
      cardNote: "Ligamento sarga 2/2 de doble trama",
      cardLink: {
        label: "Especificaciones",
        to: "/TwillDutchWeavePage#specifications",
      },
      specs: [
        { label: "Rango de Malla", value: "20×250 – 400×2800" },
        { label: "Retención Nominal", value: "~1 – 225 µm nominal" },
        { label: "Estructura", value: "Paquete Twill Dutch 2/2" },
        { label: "Aleaciones", value: "AISI 304, 304L, 316, 316L" },
      ],
    },
    overview: {
      eyebrow: "Retención Submicrométrica",
      heading: "Doble Densidad de Trama para Presiones Diferenciales Severas",
      paragraphs: [
        "El tejido Twill Dutch (reps cruzado o köpertresse) mejora el concepto del tejido Dutch liso al incorporar un ligamento en sarga 2/2. Esta técnica reduce la fatiga mecánica en los puntos de cruce del alambre, permitiendo empaquetar el doble de alambres de trama.",
        "La tela resultante forma una densa capa doble de alambres que bloquea totalmente la luz directa. Los fluidos se ven forzados a atravesar microcanales tortuosos formados entre los alambres, logrando una retención precisa de hasta 1 micra.",
        "Debido a que soporta presiones diferenciales extremas sin deformación de poros, se utiliza ampliamente en hilatura de polímeros, sistemas hidráulicos y aviación. Mallas de 325×2300 o más finas son siempre de ligamento cruzado sarga.",
      ],
      keyPoints: [
        "La disposición en sarga duplica la densidad de trama sin paso de luz directo, ofreciendo micro-retención de hasta 1 micra bajo severas presiones diferenciales.",
        "Canales microscópicos curvos que brindan gran capacidad de retención de suciedad sin paso de sólidos.",
        "Rango de fabricación de 20×250 a 400×2800 (nominal ~1–225 µm; especificaciones a partir de 325×2300 son siempre sarga).",
      ],
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/twill-dutch-weave.webp",
        alt: "Superficie de tejido Twill Dutch de precisión",
      },
    },
    specTable: {
      heading: "Especificaciones Estándar Twill Dutch",
      intro: "Datos representativos de mallas metálicas Twill Dutch conformes a ASTM E2016 e ISO 9044.",
      columns: [
        { key: "mesh", label: "Malla (Urdimbre × Trama)", align: "left" },
        { key: "warpWire", label: "Hilo Urdimbre (mm)", align: "right" },
        { key: "weftWire", label: "Hilo Trama (mm)", align: "right" },
        { key: "retention", label: "Retención Nominal (µm)", align: "right" },
        { key: "standard", label: "Norma", align: "right" },
      ],
      rows: [
        {
          id: "row-td-1",
          mesh: "20 × 250",
          warpWire: "0.25",
          weftWire: "0.20",
          retention: "200 – 225",
          standard: "ASTM E2016",
        },
        {
          id: "row-td-2",
          mesh: "30 × 350",
          warpWire: "0.20",
          weftWire: "0.14",
          retention: "140 – 160",
          standard: "ASTM E2016",
        },
        {
          id: "row-td-3",
          mesh: "80 × 700",
          warpWire: "0.10",
          weftWire: "0.07",
          retention: "40 – 45",
          standard: "ISO 9044",
        },
        {
          id: "row-td-4",
          mesh: "165 × 1400",
          warpWire: "0.07",
          weftWire: "0.04",
          retention: "15 – 18",
          standard: "ASTM E2016",
        },
        {
          id: "row-td-5",
          mesh: "200 × 1400",
          warpWire: "0.07",
          weftWire: "0.04",
          retention: "10 – 12",
          standard: "ASTM E2016",
        },
        {
          id: "row-td-6",
          mesh: "325 × 2300",
          warpWire: "0.038",
          weftWire: "0.025",
          retention: "2 – 3",
          standard: "ASTM E2016",
        },
        {
          id: "row-td-7",
          mesh: "400 × 2800",
          warpWire: "0.030",
          weftWire: "0.018",
          retention: "1 – 2",
          standard: "ASTM E2016",
        },
      ],
      caption: "Retención nominal determinada según ensayos de burbuja conformes con ASTM E2016.",
    },
    features: {
      heading: "Características Técnicas Clave",
      intro: "Ventajas mecánicas del tejido Twill Dutch en procesos de alta exigencia.",
      items: [
        {
          title: "Máxima Densidad de Hilos",
          text: "El ligamento 2/2 aloja la mayor cantidad de hilos de trama por pulgada en telas monocapa.",
        },
        {
          title: "Resistencia a la Presión",
          text: "Múltiples zonas de apoyo entre alambres distribuyen las tensiones evitando la deformación de poros.",
        },
        {
          title: "Filtración en Profundidad Tortuosa",
          text: "Canales tridimensionales que atrapan sólidos irregulares evitando la migración de partículas.",
        },
        {
          title: "Aleaciones de Grado Industrial",
          text: "Alambres de acero inoxidable AISI 304, 304L, 316 y 316L estables a temperaturas elevadas.",
        },
      ],
    },
    related: {
      heading: "Telas Filtrantes Dutch Relacionadas",
      intro: "Compare otras construcciones dentro de la familia de mallas Dutch.",
      cards: [
        {
          title: "Plain Dutch Weave",
          text: "Hilos de urdimbre gruesos con alambres de trama fina compactada forman poros triangulares uniformes para retención predecible y descarga de torta.",
          to: "/PlainDutchWeavePage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp",
            alt: "Tela Plain Dutch Weave",
          },
          badge: "8×85 – 80×400 (hasta 165×1200)",
          linkLabel: "Ver Plain Dutch",
        },
        {
          title: "Reverse Dutch Weave",
          text: "Urdidumbre de alta densidad combinada con alambres de trama más gruesos ofrece gran resistencia a la tracción para cambiadores de pantalla continuos y extrusión.",
          to: "/ReverseDutchWeavePage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-macro-photograph-of-reverse-dutch-52764d.png",
            alt: "Tela Reverse Dutch Weave",
          },
          badge: "72×15 – 325×38",
          linkLabel: "Ver Reverse Dutch",
        },
        {
          title: "Resumen de Tela Filtrante",
          text: "Visión global de las opciones de tejido Dutch, aleaciones y métodos de confección industrial.",
          to: "/FilterClothPage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp?v=djofi647st4",
            alt: "Resumen de telas filtrantes",
          },
          badge: "1 µm – 310 µm",
          linkLabel: "Página Principal",
        },
      ],
    },
    cta: {
      id: "twill-dutch-cta",
      eyebrow: "Filtración Micrométrica de Precisión",
      heading: "Solicite Presupuesto de Malla Twill Dutch",
      lead: "Disponemos de rollos continuos, cintas cortadas y discos estampados en acero inoxidable.",
      secondaryLabel: "Ver Tela Filtrante",
      secondaryTo: "/FilterClothPage",
      note: "Informes de prueba de material disponibles cuando se especifiquen en el pedido.",
    },
  },
  de: {
    breadcrumbKey: "TwillDutchWeave",
    relatedTopic: "woven",
    hero: {
      categoryBadge: "Tressengewebe",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Köpertresse (Twill Dutch Weave)",
      lead: "Durch die 2/2-Köperbindung mit doppelter Schussdichte erzielt die Köpertresse Feinstfiltration ab 1 µm unter anspruchsvollen Differenzdrücken – vollkommen ohne geradlinige Direktdurchsicht.",
      secondaryCta: {
        label: "Filtertuch-Übersicht",
        to: "/FilterClothPage",
      },
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/twill-dutch-weave.webp",
        alt: "Köpertresse Filtertuch aus Edelstahl",
      },
      cardNote: "2/2 Köperbindung Doppelschuss",
      cardLink: {
        label: "Spezifikationen",
        to: "/TwillDutchWeavePage#specifications",
      },
      specs: [
        { label: "Maschenbereich", value: "20×250 – 400×2800" },
        { label: "Filterfeinheit", value: "~1 – 225 µm nominal" },
        { label: "Bindung", value: "2/2 Köpertresse dicht" },
        { label: "Legierungen", value: "AISI 304, 304L, 316, 316L" },
      ],
    },
    overview: {
      eyebrow: "Feinstpartikel-Rückhaltung",
      heading: "Doppelte Schussdichte für anspruchsvolle Differenzdrücke",
      paragraphs: [
        "Die Köpertresse (Twill Dutch Weave) erweitert das Prinzip der glatten Tresse durch eine 2/2-Köperbindung. Diese Verwebung reduziert die Biegespannungen der Metalldrähte und erlaubt es, doppelt so viele Schussdrähte pro Längeneinheit einzuschlagen.",
        "Das Gewebe besitzt eine geschlossene Doppellage von Schussdrähten, die jegliche Direktdurchsicht blockiert. Flüssigkeiten und Gase durchströmen gewundene Mikroporen, wodurch zuverlässige Abscheidegrade ab 1 Mikrometer erreicht werden.",
        "Dank ihrer Belastbarkeit gegen Druckspitzen findet die Köpertresse breite Anwendung in Polymerschmelzefiltern, Hydrauliksystemen und der Luftfahrt. Maschenzahlen ab 325×2300 werden ausnahmslos in Köperbindung gewoben.",
      ],
      keyPoints: [
        "Die köperbindige Kettanordnung verdoppelt die Schussdichte ohne Direktdurchsicht und bietet Feinstfiltration ab 1 Mikrometer unter hohem Differenzdruck.",
        "Gewundene Strömungskanäle garantieren hohe Schmutzaufnahmekapazität und Durchbruchsicherheit.",
        "Maschenbereich 20×250 bis 400×2800 (nominal ~1–225 µm; Spezifikationen ab 325×2300 ausschließlich in Köperbindung).",
      ],
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/twill-dutch-weave.webp",
        alt: "Gewebeoberfläche Köpertresse",
      },
    },
    specTable: {
      heading: "Standardspezifikationen Köpertresse",
      intro: "Fertigungsparameter für Edelstahl-Köpertressen nach ASTM E2016 und ISO 9044.",
      columns: [
        { key: "mesh", label: "Maschen (Kette × Schuss)", align: "left" },
        { key: "warpWire", label: "Kett-Draht (mm)", align: "right" },
        { key: "weftWire", label: "Schuss-Draht (mm)", align: "right" },
        { key: "retention", label: "Filterfeinheit (µm)", align: "right" },
        { key: "standard", label: "Norm", align: "right" },
      ],
      rows: [
        {
          id: "row-td-1",
          mesh: "20 × 250",
          warpWire: "0.25",
          weftWire: "0.20",
          retention: "200 – 225",
          standard: "ASTM E2016",
        },
        {
          id: "row-td-2",
          mesh: "30 × 350",
          warpWire: "0.20",
          weftWire: "0.14",
          retention: "140 – 160",
          standard: "ASTM E2016",
        },
        {
          id: "row-td-3",
          mesh: "80 × 700",
          warpWire: "0.10",
          weftWire: "0.07",
          retention: "40 – 45",
          standard: "ISO 9044",
        },
        {
          id: "row-td-4",
          mesh: "165 × 1400",
          warpWire: "0.07",
          weftWire: "0.04",
          retention: "15 – 18",
          standard: "ASTM E2016",
        },
        {
          id: "row-td-5",
          mesh: "200 × 1400",
          warpWire: "0.07",
          weftWire: "0.04",
          retention: "10 – 12",
          standard: "ASTM E2016",
        },
        {
          id: "row-td-6",
          mesh: "325 × 2300",
          warpWire: "0.038",
          weftWire: "0.025",
          retention: "2 – 3",
          standard: "ASTM E2016",
        },
        {
          id: "row-td-7",
          mesh: "400 × 2800",
          warpWire: "0.030",
          weftWire: "0.018",
          retention: "1 – 2",
          standard: "ASTM E2016",
        },
      ],
      caption: "Nominale Trenngrenzen ermittelt über Bubble-Point-Messungen nach ASTM E2016.",
    },
    features: {
      heading: "Konstruktive Stärken",
      intro: "Vorteile der Köpertresse bei anspruchsvollen Filtrationsaufgaben.",
      items: [
        {
          title: "Höchste Schussdichte",
          text: "Ermöglicht die maximale Drahtanzahl pro Zoll aller einlagigen Gewebe.",
        },
        {
          title: "Belastbar bei Druckstößen",
          text: "Die dichte Drahtverflechtung fängt Scherbelastungen im Fluid ab.",
        },
        {
          title: "Tiefenwirksamer Rückhalt",
          text: "Gewundene Porenstrukturen halten auch nichtsphärische Partikel zuverlässig fest.",
        },
        {
          title: "Hochwertige Werkstoffe",
          text: "Hergestellt aus AISI 304, 304L, 316 und 316L für thermische und chemische Beständigkeit.",
        },
      ],
    },
    related: {
      heading: "Verwandte Gewebetypen",
      intro: "Vergleichen Sie die Köpertresse mit weiteren Tressenausführungen.",
      cards: [
        {
          title: "Plain Dutch Weave",
          text: "Starke Kettendrähte und dicht geschlagene feine Schussdrähte bilden eine einheitliche dreieckige Porengeometrie für zuverlässigen Rückhalt und leichten Kuchenabwurf.",
          to: "/PlainDutchWeavePage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp",
            alt: "Glatte Tresse Filtergewebe",
          },
          badge: "8×85 – 80×400 (bis 165×1200)",
          linkLabel: "Zur glatten Tresse",
        },
        {
          title: "Reverse Dutch Weave",
          text: "Dichte Kettendrähte gepaart mit stärkeren Schussdrähten bieten extreme Längszugfestigkeit für kontinuierliche Siebbänder und Hochdruck-Polymerschmelzefiltration.",
          to: "/ReverseDutchWeavePage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-macro-photograph-of-reverse-dutch-52764d.png",
            alt: "Panzertresse Filtergewebe",
          },
          badge: "72×15 – 325×38",
          linkLabel: "Zur Panzertresse",
        },
        {
          title: "Filtertuch-Übersicht",
          text: "Umfassende Übersicht über Tressengewebe, Legierungsauswahl und Konfektionsmöglichkeiten.",
          to: "/FilterClothPage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp?v=djofi647st4",
            alt: "Filtertuch Übersicht",
          },
          badge: "1 µm – 310 µm",
          linkLabel: "Übergeordnete Seite",
        },
      ],
    },
    cta: {
      id: "twill-dutch-cta",
      eyebrow: "Mikrofiltration im B2B-Bereich",
      heading: "Köpertresse für Ihre Präzisionsfiltration",
      lead: "MutualFilter fertigt Rollen, Streifen und gestanzte Scheiben nach Ihren Vorgaben.",
      secondaryLabel: "Filtertuch-Übersicht",
      secondaryTo: "/FilterClothPage",
      note: "Werksprüfzeugnisse auf Anfrage bei Auftragserteilung erhältlich.",
    },
  },
  fr: {
    breadcrumbKey: "TwillDutchWeave",
    relatedTopic: "woven",
    hero: {
      categoryBadge: "Toile Métallique Reps",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Toile Twill Dutch (Reps Sergé)",
      lead: "Associant une armure sergée deux sur / deux sous à une double densité de fils de trame, la toile Twill Dutch assure une filtration fine jusqu'à 1 µm sous fortes pressions différentielles sans passage direct de lumière.",
      secondaryCta: {
        label: "Voir Toutes les Toiles",
        to: "/FilterClothPage",
      },
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/twill-dutch-weave.webp",
        alt: "Toile métallique inox Twill Dutch",
      },
      cardNote: "Armure sergée 2/2 double trame",
      cardLink: {
        label: "Spécifications",
        to: "/TwillDutchWeavePage#specifications",
      },
      specs: [
        { label: "Plage de Maillage", value: "20×250 – 400×2800" },
        { label: "Seuil Nominal", value: "~1 – 225 µm nominal" },
        { label: "Armure", value: "Reps croisé 2/2 serré" },
        { label: "Alliages Standard", value: "AISI 304, 304L, 316, 316L" },
      ],
    },
    overview: {
      eyebrow: "Rétention Submicronique",
      heading: "Double Densité de Trame pour Pressions Différentielles Élevées",
      paragraphs: [
        "Le tissage Twill Dutch (reps croisé ou sergé) perfectionne le reps uni grâce à un entrecroisement sergé 2/2. Cette géométrie réduit les contraintes de courbure des fils métalliques et permet d'insérer le double de fils de trame par unité de longueur.",
        "Le tissu présente une double couche de fils de trame imbriqués qui élimine toute ligne de visée directe. Les fluides traversent des canaux micrométriques tortueux pour une coupure de filtration dès 1 micron.",
        "Résistante aux différentiels de pression élevés sans déplacement de maille, la toile Twill Dutch est incontournable dans l'extrusion de polymères, les circuits hydrauliques et l'aviation. Les maillages 325×2300 et plus fins sont exclusivement réalisés en sergé.",
      ],
      keyPoints: [
        "L'armure sergée permet de doubler la densité de trame sans passage direct de lumière, assurant une filtration fine dès 1 micron sous forte pression différentielle.",
        "Canaux micro-tortueux procurant une excellente capacité de rétention sous flux pulsant.",
        "Plage de maillage de 20×250 à 400×2800 (nominal ~1–225 µm; tissages dès 325×2300 exclusivement en sergé).",
      ],
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/twill-dutch-weave.webp",
        alt: "Détail de la toile Twill Dutch",
      },
    },
    specTable: {
      heading: "Spécifications Standards Twill Dutch",
      intro: "Paramètres de fabrication de la toile métallique reps sergé conformes aux normes ASTM E2016 et ISO 9044.",
      columns: [
        { key: "mesh", label: "Maillage (Chaîne × Trame)", align: "left" },
        { key: "warpWire", label: "Fil Chaîne (mm)", align: "right" },
        { key: "weftWire", label: "Fil Trame (mm)", align: "right" },
        { key: "retention", label: "Seuil Nominal (µm)", align: "right" },
        { key: "standard", label: "Norme", align: "right" },
      ],
      rows: [
        {
          id: "row-td-1",
          mesh: "20 × 250",
          warpWire: "0.25",
          weftWire: "0.20",
          retention: "200 – 225",
          standard: "ASTM E2016",
        },
        {
          id: "row-td-2",
          mesh: "30 × 350",
          warpWire: "0.20",
          weftWire: "0.14",
          retention: "140 – 160",
          standard: "ASTM E2016",
        },
        {
          id: "row-td-3",
          mesh: "80 × 700",
          warpWire: "0.10",
          weftWire: "0.07",
          retention: "40 – 45",
          standard: "ISO 9044",
        },
        {
          id: "row-td-4",
          mesh: "165 × 1400",
          warpWire: "0.07",
          weftWire: "0.04",
          retention: "15 – 18",
          standard: "ASTM E2016",
        },
        {
          id: "row-td-5",
          mesh: "200 × 1400",
          warpWire: "0.07",
          weftWire: "0.04",
          retention: "10 – 12",
          standard: "ASTM E2016",
        },
        {
          id: "row-td-6",
          mesh: "325 × 2300",
          warpWire: "0.038",
          weftWire: "0.025",
          retention: "2 – 3",
          standard: "ASTM E2016",
        },
        {
          id: "row-td-7",
          mesh: "400 × 2800",
          warpWire: "0.030",
          weftWire: "0.018",
          retention: "1 – 2",
          standard: "ASTM E2016",
        },
      ],
      caption: "Seuils nominaux basés sur les tests au point de bulle selon ASTM E2016.",
    },
    features: {
      heading: "Avantages Techniques",
      intro: "Points forts de l'armure reps sergé dans les procédés de séparation critique.",
      items: [
        {
          title: "Compacité Maximale",
          text: "Permet la plus forte concentration de fils par pouce parmi toutes les toiles monocouches.",
        },
        {
          title: "Tenue Sous Pression",
          text: "La structure croisée absorbe les contraintes de cisaillement et préserve l'intégrité des pores.",
        },
        {
          title: "Rétention Tortueuse",
          text: "Canaux sinueux qui retiennent efficacement les particules non sphériques.",
        },
        {
          title: "Alliages Haute Qualité",
          text: "Fabriquée en AISI 304, 304L, 316 et 316L pour une tenue thermique et chimique fiable.",
        },
      ],
    },
    related: {
      heading: "Autres Toiles Reps",
      intro: "Comparez le reps sergé avec les autres variantes de toiles filtrantes.",
      cards: [
        {
          title: "Plain Dutch Weave",
          text: "Fils de chaîne robustes associés à une trame fine serrée formant des pores triangulaires réguliers pour une rétention prévisible et un démoulage aisé du gâteau.",
          to: "/PlainDutchWeavePage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp",
            alt: "Toile Plain Dutch",
          },
          badge: "8×85 – 80×400 (jusqu'à 165×1200)",
          linkLabel: "Voir Plain Dutch",
        },
        {
          title: "Reverse Dutch Weave",
          text: "Fils de chaîne denses combinés à des trames plus épaisses pour une résistance longitudinale élevée sur bandes de changeurs continus et filtration de polymères.",
          to: "/ReverseDutchWeavePage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-macro-photograph-of-reverse-dutch-52764d.png",
            alt: "Toile Reverse Dutch",
          },
          badge: "72×15 – 325×38",
          linkLabel: "Voir Reverse Dutch",
        },
        {
          title: "Vue d'ensemble Toiles Filtrantes",
          text: "Présentation globale des armures reps, des critères de sélection et des fabrications sur mesure.",
          to: "/FilterClothPage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp?v=djofi647st4",
            alt: "Vue d'ensemble toiles reps",
          },
          badge: "1 µm – 310 µm",
          linkLabel: "Page Parent",
        },
      ],
    },
    cta: {
      id: "twill-dutch-cta",
      eyebrow: "Filtration Fine Industrielle",
      heading: "Consultez MutualFilter pour Vos Toiles Twill Dutch",
      lead: "Nos équipes fabriquent rouleaux, bandes découpées et disques de filtration selon votre cahier des charges.",
      secondaryLabel: "Voir Toiles Filtrantes",
      secondaryTo: "/FilterClothPage",
      note: "Certificats matière disponibles lorsque spécifiés dans la commande.",
    },
  },
  pt: {
    breadcrumbKey: "TwillDutchWeave",
    relatedTopic: "woven",
    hero: {
      categoryBadge: "Tecido Filtrante Holandês",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Tela em Trama Holandesa Sarjada",
      lead: "Com padrão em sarja 2/2 e dupla densidade de fios de trama, a trama holandesa sarjada atinge microrretenção a partir de 1 µm sob pressões diferenciais severas, com bloqueio total de linha reta.",
      secondaryCta: {
        label: "Ver Tecidos Filtrantes",
        to: "/FilterClothPage",
      },
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/twill-dutch-weave.webp",
        alt: "Tela metálica em trama holandesa sarjada",
      },
      cardNote: "Sarja 2/2 de dupla trama",
      cardLink: {
        label: "Especificações",
        to: "/TwillDutchWeavePage#specifications",
      },
      specs: [
        { label: "Faixa de Malha", value: "20×250 – 400×2800" },
        { label: "Retenção Nominal", value: "~1 – 225 µm nominal" },
        { label: "Estrutura", value: "Trama sarjada 2/2 compacta" },
        { label: "Ligas Padrão", value: "AISI 304, 304L, 316, 316L" },
      ],
    },
    overview: {
      eyebrow: "Retenção Submicrométrica",
      heading: "Dupla Densidade de Trama para Altas Pressões Diferenciais",
      paragraphs: [
        "A trama holandesa sarjada (Twill Dutch Weave) aprimora a versão lisa pelo entrelaçamento em sarja 2 por cima / 2 por baixo. Essa configuração reduz a tensão de flexão nos fios, permitindo compactar o dobro de fios de trama por polegada.",
        "O tecido resultante forma uma camada dupla densa de fios de trama sem passagem direta de luz. O fluxo é direcionado por canais microscópicos sinuosos, garantindo retenção precisa de até 1 mícron.",
        "Com elevada resistência a ciclos de pressão hidráulica, é amplamente empregada na filtração de polímeros fundidos, circuitos hidráulicos e aviação. Malhas a partir de 325×2300 são fabricadas unicamente em sarja.",
      ],
      keyPoints: [
        "A urdidura em sarja dobra a densidade da trama sem linha de visão direta, garantindo microrretenção a partir de 1 mícron sob pressões diferenciais severas.",
        "Canais microtortuosos que fornecem grande capacidade de retenção de impurezas.",
        "Faixa de malha de 20×250 a 400×2800 (nominal ~1–225 µm; tramas a partir de 325×2300 são exclusivamente em sarja).",
      ],
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/twill-dutch-weave.webp",
        alt: "Superfície da trama holandesa sarjada",
      },
    },
    specTable: {
      heading: "Especificações Padrão Twill Dutch",
      intro: "Dados de fabricação para telas em trama holandesa sarjada em conformidade com ASTM E2016 e ISO 9044.",
      columns: [
        { key: "mesh", label: "Malha (Urdume × Trama)", align: "left" },
        { key: "warpWire", label: "Fio Urdume (mm)", align: "right" },
        { key: "weftWire", label: "Fio Trama (mm)", align: "right" },
        { key: "retention", label: "Retenção Nominal (µm)", align: "right" },
        { key: "standard", label: "Norma", align: "right" },
      ],
      rows: [
        {
          id: "row-td-1",
          mesh: "20 × 250",
          warpWire: "0.25",
          weftWire: "0.20",
          retention: "200 – 225",
          standard: "ASTM E2016",
        },
        {
          id: "row-td-2",
          mesh: "30 × 350",
          warpWire: "0.20",
          weftWire: "0.14",
          retention: "140 – 160",
          standard: "ASTM E2016",
        },
        {
          id: "row-td-3",
          mesh: "80 × 700",
          warpWire: "0.10",
          weftWire: "0.07",
          retention: "40 – 45",
          standard: "ISO 9044",
        },
        {
          id: "row-td-4",
          mesh: "165 × 1400",
          warpWire: "0.07",
          weftWire: "0.04",
          retention: "15 – 18",
          standard: "ASTM E2016",
        },
        {
          id: "row-td-5",
          mesh: "200 × 1400",
          warpWire: "0.07",
          weftWire: "0.04",
          retention: "10 – 12",
          standard: "ASTM E2016",
        },
        {
          id: "row-td-6",
          mesh: "325 × 2300",
          warpWire: "0.038",
          weftWire: "0.025",
          retention: "2 – 3",
          standard: "ASTM E2016",
        },
        {
          id: "row-td-7",
          mesh: "400 × 2800",
          warpWire: "0.030",
          weftWire: "0.018",
          retention: "1 – 2",
          standard: "ASTM E2016",
        },
      ],
      caption: "Retenção nominal aferida por ensaios de ponto de bolha em acordo com a ASTM E2016.",
    },
    features: {
      heading: "Destaques Construtivos",
      intro: "Vantagens operacionais da trama holandesa sarjada em separação de precisão.",
      items: [
        {
          title: "Alta Concentração de Fios",
          text: "O padrão 2/2 atinge a maior densidade de fios de trama por polegada em tecidos de camada única.",
        },
        {
          title: "Tolerância a Picos de Carga",
          text: "A disposição cruzada dissipa as tensões cisalhantes preservando a estabilidade dos microporos.",
        },
        {
          title: "Retenção Sinuosa",
          text: "Canais tridimensionais que retêm partículas de geometria irregular de forma consistente.",
        },
        {
          title: "Aços Inoxidáveis Confiáveis",
          text: "Confeccionada em AISI 304, 304L, 316 e 316L para resistência térmica e química.",
        },
      ],
    },
    related: {
      heading: "Outras Tramas Filtrantes",
      intro: "Compare a trama holandesa sarjada com as outras opções da família.",
      cards: [
        {
          title: "Plain Dutch Weave",
          text: "Fios de urdume grossos combinados com trama fina compactada produzem geometria uniforme de poros triangulares para retenção previsível e fácil descarte de torta.",
          to: "/PlainDutchWeavePage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp",
            alt: "Tecido Plain Dutch Weave",
          },
          badge: "8×85 – 80×400 (até 165×1200)",
          linkLabel: "Ver Plain Dutch",
        },
        {
          title: "Reverse Dutch Weave",
          text: "Urdumes densos combinados com arames de trama mais grossos proporcionam extrema resistência à tração para fitas contínuas e filtração de polímeros sob alta pressão.",
          to: "/ReverseDutchWeavePage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-macro-photograph-of-reverse-dutch-52764d.png",
            alt: "Tecido Reverse Dutch Weave",
          },
          badge: "72×15 – 325×38",
          linkLabel: "Ver Reverse Dutch",
        },
        {
          title: "Visão Geral de Tecidos Filtrantes",
          text: "Panorama geral das tramas holandesas, critérios de seleção de ligas e configurações especiais.",
          to: "/FilterClothPage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp?v=djofi647st4",
            alt: "Tecidos filtrantes visão geral",
          },
          badge: "1 µm – 310 µm",
          linkLabel: "Página Principal",
        },
      ],
    },
    cta: {
      id: "twill-dutch-cta",
      eyebrow: "Microrretenção de Precisão",
      heading: "Consulte a MutualFilter para Trama Holandesa Sarjada",
      lead: "Fornecemos bobinas mestre, fitas cortadas e discos estampados sob encomenda.",
      secondaryLabel: "Ver Tecidos Filtrantes",
      secondaryTo: "/FilterClothPage",
      note: "Relatórios de ensaio de material disponíveis mediante solicitação no pedido.",
    },
  },
};
